import { Routes } from '@angular/router';
import { canActivateAuthorization } from '../security/authorization.guard';
import { canActivateErweiterung } from '../security/erweiterung.guard';
import { ErweiterungConfigResponse, PermissionResponse } from '../core/base-api';
import { MeldungsempfaengerSucheComponent } from './meldungsempfaenger/meldungsempfaenger-suche/meldungsempfaenger-suche.component';
import { MeldungsempfaengerListComponent } from './meldungsempfaenger/meldungsempfaenger-list/meldungsempfaenger-list.component';
import { MeldungsempfaengerDetailComponent } from './meldungsempfaenger/meldungsempfaenger-detail/meldungsempfaenger-detail.component';
import { ViewMode } from './meldungsempfaenger/view-mode.enum';
import { MeldungsempfaengerResolver } from './meldungsempfaenger/meldungsempfaenger-resolver';
import { canDeactivateDirtyCheck } from '../shared/guards/dirty-check.guard';
import { MeldungsempfaengerListResolver } from './meldungsempfaenger/meldungsempfaenger-list-resolver';
import { Ech0134GrundbuchmeldungSucheComponent } from './grundbuchmeldung/grundbuchmeldung-suche/ech0134-grundbuchmeldung-suche.component';
import { Ech0134GrundbuchmeldungListComponent } from './grundbuchmeldung/grundbuchmeldung-list/ech0134-grundbuchmeldung-list.component';
import { GrundbuchmeldungListResolver } from './grundbuchmeldung/grundbuchmeldung-list-resolver';
import { keysToTranslate } from '../../i18n/keys-to-translate';
import PermissionsEnum = PermissionResponse.PermissionsEnum;
import ActiveErweiterungenEnum = ErweiterungConfigResponse.ActiveErweiterungenEnum;

export const Ech0134Routes: Routes = [
  {
    path: 'ech0134',
    loadChildren: () => routes,
    providers: [MeldungsempfaengerResolver, MeldungsempfaengerListResolver, GrundbuchmeldungListResolver]
  }
];

const routes: Routes = [
  {
    path: '',
    pathMatch: 'prefix',
    redirectTo: 'grundbuchmeldungen'
  },
  {
    path: 'grundbuchmeldungen',
    canActivate: [canActivateAuthorization, canActivateErweiterung],
    data: {
      permissions: [PermissionsEnum.ECH_0134_SUCHEN, PermissionsEnum.ECH_0134_VERWALTEN_MELDUNG],
      erweiterung: [ActiveErweiterungenEnum.ECH_0134]
    },
    children: [
      {
        path: '',
        pathMatch: 'prefix',
        redirectTo: 'suche'
      },
      {
        path: 'suche',
        component: Ech0134GrundbuchmeldungSucheComponent,
        data: {
          breadcrumb: 'core.navigation.ech0134.grundbuchmeldungen',
          breadcrumbStatus: 'firstLevel'
        }
      },
      {
        path: 'list',
        component: Ech0134GrundbuchmeldungListComponent,
        data: {
          breadcrumb: 'shared.breadcrumb.list',
          breadcrumbStatus: 'secondLevel'
        },
        resolve: {
          listLoader: GrundbuchmeldungListResolver
        }
      }
    ]
  },
  {
    path: 'meldungsempfaenger',
    canActivate: [canActivateAuthorization, canActivateErweiterung],
    data: {
      permissions: [PermissionsEnum.ECH_0134_VERWALTEN_EMPFAENGER],
      erweiterung: [ActiveErweiterungenEnum.ECH_0134]
    },
    children: [
      {
        path: '',
        pathMatch: 'prefix',
        redirectTo: 'suche'
      },
      {
        path: 'suche',
        component: MeldungsempfaengerSucheComponent,
        data: {
          breadcrumb: 'meldungsempfaenger.suche',
          breadcrumbStatus: 'firstLevel'
        }
      },
      {
        path: 'list',
        component: MeldungsempfaengerListComponent,
        data: {
          breadcrumb: 'shared.breadcrumb.list',
          breadcrumbStatus: 'secondLevel'
        },
        resolve: {
          listLoader: MeldungsempfaengerListResolver
        }
      },
      {
        path: 'new',
        component: MeldungsempfaengerDetailComponent,
        canDeactivate: [canDeactivateDirtyCheck],
        data: {
          viewMode: ViewMode.NEW
        }
      },
      {
        path: 'detail/:id',
        component: MeldungsempfaengerDetailComponent,
        canDeactivate: [canDeactivateDirtyCheck],
        data: {
          viewMode: ViewMode.READ_ONLY
        },
        resolve: {
          entity: MeldungsempfaengerResolver
        }
      },
      {
        path: 'edit/:id',
        component: MeldungsempfaengerDetailComponent,
        canDeactivate: [canDeactivateDirtyCheck],
        data: {
          viewMode: ViewMode.EDIT
        },
        resolve: {
          entity: MeldungsempfaengerResolver
        }
      }
    ]
  }
];

keysToTranslate([
  'grundbuchmeldung.meldungsstatus.NICHT_ERSTELLT',
  'grundbuchmeldung.meldungsstatus.ERSTELLT',
  'grundbuchmeldung.meldungsstatus.FEHLERHAFT_ERSTELLT',
  'grundbuchmeldung.meldungsstatus.FEHLERHAFT_GESENDET',
  'grundbuchmeldung.meldungsstatus.GESENDET',
  'meldungsempfaenger.ereignisse.NEU_LIG_SDR',
  'meldungsempfaenger.ereignisse.LOESCHUNG_LIG_SDR',
  'meldungsempfaenger.ereignisse.AENDERUNG_LIG_SDR',
  'meldungsempfaenger.ereignisse.NEU_STW_MIT',
  'meldungsempfaenger.ereignisse.LOESCHUNG_STW_MIT',
  'meldungsempfaenger.ereignisse.AENDERUNG_STW_MIT',
  'meldungsempfaenger.ereignisse.NEU_EIGENTUM',
  'meldungsempfaenger.ereignisse.LOESCHUNG_EIGENTUM',
  'meldungsempfaenger.ereignisse.AENDERUNG_EIGENTUM',
  'meldungsempfaenger.ereignisse.NEU_ANMERKUNG',
  'meldungsempfaenger.ereignisse.LOESCHUNG_ANMERKUNG',
  'meldungsempfaenger.ereignisse.AENDERUNG_ANMERKUNG',
  'meldungsempfaenger.ereignisse.NEU_DIENSTBARKEIT',
  'meldungsempfaenger.ereignisse.LOESCHUNG_DIENSTBARKEIT',
  'meldungsempfaenger.ereignisse.AENDERUNG_DIENSTBARKEIT',
  'meldungsempfaenger.ereignisse.NEU_GRUNDLAST',
  'meldungsempfaenger.ereignisse.LOESCHUNG_GRUNDLAST',
  'meldungsempfaenger.ereignisse.AENDERUNG_GRUNDLAST',
  'meldungsempfaenger.ereignisse.NEU_VORMERKUNG',
  'meldungsempfaenger.ereignisse.LOESCHUNG_VORMERKUNG',
  'meldungsempfaenger.ereignisse.AENDERUNG_VORMERKUNG',
  'meldungsempfaenger.ereignisse.NEU_PFANDRECHT',
  'meldungsempfaenger.ereignisse.LOESCHUNG_PFANDRECHT',
  'meldungsempfaenger.ereignisse.AENDERUNG_PFANDRECHT',
  'meldungsempfaenger.hasMeldungen',
  'meldungsempfaenger.error.uniqueConstraint',
  'core.navigation.ech0134.grundbuchmeldungen',
  'core.navigation.ech0134.empfaenger',
  'tooltip.meldungsempfaenger',
  'ech0134.grundbuchmeldungen.title',
  'ech0134.grundbuchmeldungen.reset.message',
  'not.authorized'
]);
